/**
 * Contact lists + bulk CSV/XLSX import hooks.
 *
 * The import flow is two-step on purpose so the user gets to confirm which
 * column is the phone before we touch the database. Step 1 (preview) is a
 * mutation rather than a query because each call re-uploads the file —
 * caching it under a query key would give us nothing useful and would
 * leak a multipart Body across components.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';

export interface ContactListItem {
  id: number;
  name: string;
  description?: string;
  contact_count: number;
  default_country: string;
  created_at: string;
}

export interface ContactItem {
  id: number;
  phone_e164: string;
  country_code?: string;
  custom_fields: Record<string, string>;
  created_at: string;
}

export interface ContactsResponse {
  list: ContactListItem;
  items: ContactItem[];
}

export interface ImportPreview {
  headers: string[];
  rows: string[][];
  total_rows: number;
  guessed_phone: string;
  detected_kind: 'csv' | 'xlsx';
}

export interface ImportResult {
  inserted: number;
  skipped: number;
  invalid: number;
}

const LISTS_KEY = ['contact-lists'] as const;
const contactsKey = (listID: number) => ['contact-lists', listID, 'contacts'] as const;
const variablesKey = (listID: number) => ['contact-lists', listID, 'variables'] as const;

// ----- Lists -----

export function useContactLists() {
  return useQuery({
    queryKey: LISTS_KEY,
    queryFn: () => api.get<{ items: ContactListItem[] }>('/v1/contact-lists'),
    staleTime: 30_000,
  });
}

export function useCreateContactList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; description?: string; default_country?: string }) =>
      api.post<ContactListItem>('/v1/contact-lists', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTS_KEY });
    },
  });
}

export function useDeleteContactList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/contact-lists/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTS_KEY });
    },
  });
}

// ----- Contacts in a list -----

interface ListContactsArgs {
  listID: number | null;
  enabled?: boolean;
}

export function useContactsInList({ listID, enabled = true }: ListContactsArgs) {
  return useQuery({
    queryKey: listID ? contactsKey(listID) : ['contact-lists', 'none', 'contacts'],
    queryFn: () => api.get<ContactsResponse>(`/v1/contact-lists/${listID}/contacts?limit=100`),
    enabled: enabled && listID !== null,
    staleTime: 30_000,
  });
}

export function useContactVariables(listID: number | null) {
  return useQuery({
    queryKey: listID ? variablesKey(listID) : ['contact-lists', 'none', 'variables'],
    queryFn: () => api.get<{ variables: string[] }>(`/v1/contact-lists/${listID}/variables`),
    enabled: listID !== null,
    staleTime: 60_000,
  });
}

// ----- Import flow (preview + run) -----

async function postMultipart<T>(path: string, formData: FormData): Promise<T> {
  // Route the multipart upload through the shared api client so it gets the
  // same 401 -> refresh -> retry handling as every other request. A long
  // import wizard can outlive the access token; a raw fetch here would 401
  // with `invalid_token` instead of refreshing. Passing the FormData via
  // `options.body` (not the JSON `body` arg) leaves the browser in charge of
  // the multipart boundary — the client only injects Authorization.
  return api.post<T>(path, undefined, { body: formData });
}

export function usePreviewImport() {
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return postMultipart<ImportPreview>('/v1/contact-lists/import-preview', fd);
    },
  });
}

export function useRunImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      listID,
      file,
      phoneColumn,
    }: {
      listID: number;
      file: File;
      phoneColumn: string;
    }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('phone_column', phoneColumn);
      return postMultipart<ImportResult>(`/v1/contact-lists/${listID}/import`, fd);
    },
    onSuccess: (_data, { listID }) => {
      void qc.invalidateQueries({ queryKey: LISTS_KEY });
      void qc.invalidateQueries({ queryKey: contactsKey(listID) });
      void qc.invalidateQueries({ queryKey: variablesKey(listID) });
    },
  });
}

/**
 * Downloads a contact list as CSV, custom variables included, so the file
 * can be edited and fed straight back through the import wizard.
 *
 * Same blob dance as downloadCampaignCSV — an `<a href>` can't carry the
 * Bearer header, so we fetch, wrap in an object URL, click a synthetic
 * anchor and revoke.
 *
 * The server names the file from the list name; we only fall back to the
 * id when that header is missing, rather than deriving a name client-side
 * that could disagree with it.
 */
export async function downloadContactListCSV(id: number): Promise<void> {
  const apiBase =
    (typeof import.meta.env.VITE_API_BASE_URL === 'string' &&
      import.meta.env.VITE_API_BASE_URL) ||
    'http://localhost:8080';
  const token = getAccessToken();
  const response = await fetch(`${apiBase}/v1/contact-lists/${id}/contacts.csv`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`CSV export failed (${response.status})`);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = match?.[1] ?? `ezsender-list-${id}-contacts.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
